import * as THREE from 'three';
import type { Rect } from '../../core/math';
import { weatherState } from '../../fx/ambientFx';
import { onMood } from '../../render/mood';
import { onQualityTier } from '../../render/qualityTier';
import type { Decor } from '../decor';
import type { SignalLights } from '../signalControl';
import type { CityLayout } from '../types';
import { buildBuildings } from './buildings';
import { buildGround, buildLampPools, PAVE_Y } from './ground';
import { backdropLots, buildLandmarks } from './landmarks';
import { createCityMaterials, type CityMaterials } from './materials';
import { ChunkedBuilders } from './meshBuilder';
import { buildLampsView, type LampsView } from './lampsView';
import { BELISHA_GLOBE, createKnockdownView, type KnockdownView } from './knockdowns';
import { createOccluderFade, OCCLUDER_THIN_UV, type OccluderFade } from './occluderFade';
import { buildProps, streetModelSet } from './props';
import { modelPack } from '../../render/models/modelLibrary';
import { createStreetAtlasUniforms, patchStreetAtlas, setStreetAtlasActive } from '../../render/models/streetModels';
import { createRiverHaze } from './riverHaze';
import { createSkyDome } from './skyDome';
import { createSkyline } from './skyline';
import { buildSignalStatics, createSignalsView, type SignalsView } from './signalsView';
import { buildStreetLife, createPuddles, type StreetLifeStats } from './streetLife';

/**
 * Builds the whole district view: merged, chunked static geometry over a small shared material set,
 * plus the few animated pieces (signals, beacons, water). Returns per-frame update + dispose.
 */
export const CHUNK_SIZE = 240;
const CHUNK_ORIGIN = -720;

const CASTS = new Set(['facade', 'props', 'metal', 'foliage']);
const RECEIVES = new Set(['facade', 'props', 'metal', 'foliage', 'asphalt', 'setts', 'paving', 'yard', 'grass', 'gravel', 'markings', 'kerb', 'kerbLite', 'water']);

/**
 * Detail by render quality tier (docs/PERF.md, "Phone performance"); HIGH draws everything as built,
 * with the same draw calls as before.
 * - Tiered detail rides at the end of its chunk's main geometry and the draw range trims it: the `mid`
 *   key is drawn on HIGH and MEDIUM, the `fine` key on HIGH only, LOW draws neither.
 *   - `props`: streetLife's `clutter` (dustbins, bikes, prams, crates, barrels, pallets, oil drums,
 *     coal bunkers) and `clutterFine` (weeds, milk bottles);
 *   - `facade`: buildings' `facadeMid` (string courses, cornices, gutters, dormers, chimney detail) and
 *     `facadeFine` (window sills, ridge tiles, flashing, brackets, dentils, roof puddles);
 *   - `foliage`: `foliageMid` (hedges) and `foliageFine` (the finer canopy blobs).
 *   Street furniture (`props`) casts shadows on HIGH only.
 * - Meshy street models (render/models/streetModels.ts, when the street pack loaded): a family's models
 *   go in the `…Model` key, drawn on MEDIUM and HIGH; its procedural stand-ins in the `…Low` key, drawn
 *   on LOW only (so LOW stays exactly as it was). Chunk geometry is [low | main | model | mid | fine] and
 *   each tier draws one contiguous range of it: no extra draw calls.
 * - Kerbs: `kerb` (granite face + individual top stones, one global mesh) on HIGH, `kerbLite` (the
 *   same face + one plain top strip) on MEDIUM and LOW.
 */
type Tier = 'low' | 'medium' | 'high';
const TIERED: { target: string; low: string; model: string; mid: string; fine: string }[] = [
  { target: 'props', low: 'propsLow', model: 'propsModel', mid: 'clutter', fine: 'clutterFine' },
  { target: 'facade', low: 'facadeLow', model: 'facadeModel', mid: 'facadeMid', fine: 'facadeFine' },
  { target: 'foliage', low: 'foliageLow', model: 'foliageModel', mid: 'foliageMid', fine: 'foliageFine' },
  { target: 'glow', low: 'glowLow', model: 'glowModel', mid: 'glowMid', fine: 'glowFine' },
];
const SHOWN: Record<string, (t: Tier) => boolean> = {
  kerb: (t) => t === 'high',
  kerbLite: (t) => t !== 'high',
};
const CAST_BY_TIER: Record<string, (t: Tier) => boolean> = {
  props: (t) => t === 'high',
};

export interface CityView {
  readonly group: THREE.Group;
  readonly materials: CityMaterials;
  readonly lamps: LampsView;
  /** Line-of-sight cut-away through buildings / canopies in front of the player. */
  readonly occluder: OccluderFade;
  /** Knocked-over lamp posts / signal poles / Belisha beacons. */
  readonly knockdowns: KnockdownView;
  readonly signalHeads: SignalsView;
  update(time: number, dt: number): void;
  stats(): { meshes: number; triangles: number; chunks: number; streetLife: StreetLifeStats; wetness: number; skyline: { cards: number; loaded: boolean } };
  dispose(): void;
}

export function buildCityView(scene: THREE.Scene, layout: CityLayout, decor: Decor, signals: SignalLights): CityView {
  const keep: Rect[] = decor.bridges.map((b) => {
    const e = layout.roads.edges[b.edgeId]!;
    const x = layout.roads.nodes[e.from]!.pos.x;
    const hw = e.width / 2 + e.sidewalk + 0.8;
    return { minX: x - hw, minZ: b.z0 - 1, maxX: x + hw, maxZ: b.z1 + 1 };
  });
  const mats = createCityMaterials(decor.water, keep);
  const beaconMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(3.2, 1.6, 0.25) });
  // Everything tall enough to hide the player gets the line-of-sight cut-away (ground does not).
  const occluder = createOccluderFade();
  for (const m of [mats.facade, mats.foliage, mats.glow, mats.signs, mats.litSigns, beaconMat]) occluder.patch(m);
  // The props chunks carry the thin iron too (lamp posts, signal poles, railings, crane members, tagged
  // through their uv below): it only loses a short section in the cut.
  for (const m of [mats.props, mats.metal]) occluder.patch(m, true);
  // The ground is never cut, but the floor seen through a hole in a building gets the night fill.
  for (const m of [mats.asphalt, mats.setts, mats.paving, mats.yard, mats.grass, mats.gravel, mats.markings, mats.kerb]) occluder.patchFill(m, 'vWorldPosCut');
  // Meshy street models (if the street pack loaded during boot) draw through the props / foliage chunks.
  const models = streetModelSet(modelPack('street'));
  // Knocked street furniture vanishes from the merged chunks (posts: metal, beacon poles: props); the
  // toppling copies are built from the same street models.
  const knockdowns = createKnockdownView(scene, mats.signAtlas, models);
  for (const m of [mats.props, mats.metal, beaconMat]) knockdowns.patch(m);
  const street = createStreetAtlasUniforms(models?.atlas ?? null);
  if (models) for (const m of [mats.props, mats.foliage]) patchStreetAtlas(m, street);
  const matByKey: Record<string, THREE.Material> = {
    facade: mats.facade,
    props: mats.props,
    metal: mats.metal,
    glow: mats.glow,
    asphalt: mats.asphalt,
    setts: mats.setts,
    paving: mats.paving,
    yard: mats.yard,
    grass: mats.grass,
    gravel: mats.gravel,
    markings: mats.markings,
    kerb: mats.kerb,
    kerbLite: mats.kerb,
    water: mats.water,
    signs: mats.signs,
    litSigns: mats.litSigns,
    pools: mats.pools,
    foliage: mats.foliage,
    beacons: beaconMat,
  };

  const cb = new ChunkedBuilders(CHUNK_SIZE, CHUNK_ORIGIN);
  buildGround(layout, decor, cb);
  buildLampPools(decor, cb);
  const lots = [...layout.buildings, ...backdropLots(layout)];
  buildBuildings(lots, { cb, signs: mats.signAtlas, roofKit: models?.roofKit ?? null });
  occluder.setBlockers(lots);
  buildProps(decor, cb, mats.signAtlas, models);
  const life = buildStreetLife(layout, decor, cb, mats.signAtlas, models?.life ?? null);
  buildLandmarks(layout, decor, cb, mats.signAtlas);
  buildSignalStatics(decor.signalHeads, cb);
  // Street name plates.
  for (const sp of decor.streetPlates) {
    const s = mats.signAtlas.add(sp.text, 'plate');
    const w = 1.5;
    const h = w / s.aspect;
    const cs = Math.cos(sp.heading);
    const sn = Math.sin(sp.heading);
    // plate faces `heading`; its text runs along the reader's right = (-cos h, sin h) rotated... use local +X
    const ux = cs;
    const uz = -sn;
    const fx = sn * 0.03;
    const fz = cs * 0.03;
    const y = 2.6;
    cb.global('signs').quad(
      [sp.pos.x - (ux * w) / 2 + fx, y, sp.pos.z - (uz * w) / 2 + fz],
      [sp.pos.x + (ux * w) / 2 + fx, y, sp.pos.z + (uz * w) / 2 + fz],
      [sp.pos.x + (ux * w) / 2 + fx, y + h, sp.pos.z + (uz * w) / 2 + fz],
      [sp.pos.x - (ux * w) / 2 + fx, y + h, sp.pos.z - (uz * w) / 2 + fz],
      s.uv,
      [1, 1, 1],
    );
  }
  // Belisha beacon globes (flashing).
  for (const z of decor.zebras) {
    for (const b of z.beacons) cb.global('beacons').cylinder(b.x, b.z, PAVE_Y + BELISHA_GLOBE.y0, PAVE_Y + BELISHA_GLOBE.y1, BELISHA_GLOBE.r, BELISHA_GLOBE.r, 10, [1, 1, 1], [0, 0, 1, 1]);
  }

  // Iron rides in its cell's props chunk (one draw call, as the old `metal → props` alias did), its unused
  // uv set to the cut-away's thin tag first so lamp posts and crane legs get the narrow capsule.
  for (const [iron, into] of [
    ['metal', 'props'],
    ['metalLow', 'propsLow'],
  ] as const) {
    for (const [chunk, b] of cb.take(iron)) {
      b.uv.fill(OCCLUDER_THIN_UV);
      cb.at(into, chunk).append(b);
    }
  }

  const group = new THREE.Group();
  group.name = 'low-borough';
  let triangles = 0;
  const chunks = new Set<string>();
  const tiered: { mesh: THREE.Mesh; key: string }[] = [];
  // Tiered detail: each chunk's geometry is [low | main | model | mid | fine] (index counts low, base, mid
  // mark the cuts); a tier draws one contiguous range of it (see the draw ranges below).
  const ranges = new Map<string, { low: number; base: number; mid: number }>();
  for (const t of TIERED) {
    const lowB = cb.take(t.low);
    const modelB = cb.take(t.model);
    const midB = cb.take(t.mid);
    const fineB = cb.take(t.fine);
    for (const chunk of new Set([...lowB.keys(), ...modelB.keys(), ...midB.keys(), ...fineB.keys()])) {
      let p = cb.at(t.target, chunk);
      const lowPart = lowB.get(chunk);
      let low = 0;
      if (lowPart) {
        // the procedural stand-ins go in front of the main geometry
        low = lowPart.indexCount;
        lowPart.append(p);
        cb.put(t.target, chunk, lowPart);
        p = lowPart;
      }
      const base = p.indexCount;
      for (const part of [modelB.get(chunk), midB.get(chunk)]) if (part) p.append(part);
      const mid = p.indexCount;
      const fine = fineB.get(chunk);
      if (fine) p.append(fine);
      ranges.set(`${t.target}|${chunk}`, { low, base, mid });
    }
  }
  const trimmed: { geo: THREE.BufferGeometry; low: number; base: number; mid: number }[] = [];
  // Chunks wholly outside the playable rectangle hold only the backdrop rows: their shadows fall on
  // ground nobody reaches, so they skip the shadow pass (the full-district overview stays in budget
  // with Kiln Common's northern backdrop, docs/PERF.md).
  const B = layout.bounds;
  const outside = (chunk: string): boolean => {
    if (chunk === 'global') return false;
    const [cx, cz] = chunk.split(',').map(Number) as [number, number];
    const x0 = CHUNK_ORIGIN + cx * CHUNK_SIZE;
    const z0 = CHUNK_ORIGIN + cz * CHUNK_SIZE;
    return x0 >= B.maxX || x0 + CHUNK_SIZE <= B.minX || z0 >= B.maxZ || z0 + CHUNK_SIZE <= B.minZ;
  };
  const noShadow = new Set<THREE.Mesh>();
  for (const [key, chunk, builder] of cb.entries()) {
    const geo = builder.build();
    if (!geo) continue;
    const mat = matByKey[key];
    if (!mat) throw new Error(`city view: no material '${key}'`);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = `${key}:${chunk}`;
    mesh.castShadow = CASTS.has(key) && !outside(chunk);
    if (outside(chunk)) noShadow.add(mesh);
    mesh.receiveShadow = RECEIVES.has(key);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    if (key === 'pools') mesh.renderOrder = 2;
    if (chunk === 'global') mesh.frustumCulled = false;
    group.add(mesh);
    triangles += (geo.index?.count ?? 0) / 3;
    chunks.add(chunk);
    if (SHOWN[key] || CAST_BY_TIER[key]) tiered.push({ mesh, key });
    const r = ranges.get(`${key}|${chunk}`);
    if (r) trimmed.push({ geo, ...r });
  }
  scene.add(group);
  const unsubTier = onQualityTier((t) => {
    mats.setTier(t);
    setStreetAtlasActive(street, models?.atlas ?? null, t !== 'low', [mats.props, mats.foliage]);
    // LOW: [low | main]; MEDIUM: [main | model | mid]; HIGH: [main | model | mid | fine].
    for (const { geo, low, base, mid } of trimmed) {
      if (t === 'low') geo.setDrawRange(0, base);
      else geo.setDrawRange(low, t === 'medium' ? mid - low : Infinity);
    }
    for (const { mesh, key } of tiered) {
      const shown = SHOWN[key];
      if (shown) mesh.visible = shown(t);
      const cast = CAST_BY_TIER[key];
      if (cast) mesh.castShadow = cast(t) && !noShadow.has(mesh);
    }
  });

  // Standing water (fades with the weather) and low mist over the canal and basin.
  const puddles = createPuddles(life.puddles, mats.puddleMask, mats.puddleNormal);
  if (puddles.mesh) {
    puddles.mesh.updateMatrix();
    scene.add(puddles.mesh);
  }
  const haze = createRiverHaze(decor, layout);
  scene.add(haze.mesh);
  // The far skyline beyond the backdrop streets (one draw call, every tier).
  const skyline = createSkyline(scene, layout);
  scene.add(skyline.mesh);
  // The sky behind it: a gradient with the mood's horizon glow and clouds, one full-screen triangle.
  const sky = createSkyDome(scene);
  scene.add(sky.mesh);
  const unsubSkyTier = onQualityTier((t) => sky.setTier(t));
  let wetness = -1;

  const signalsView = createSignalsView(scene, decor.signalHeads, signals, mats.glowTexture);
  const lampsView = buildLampsView(scene, decor, mats.glowTexture, { lots: layout.buildings, ripple: mats.waterNormal });
  const unsubLampTier = onQualityTier((t) => lampsView.setTier(t));
  // the baked ripple and puddle alpha replace their placeholders once loaded
  void mats.ready.then(() => {
    puddles.setTextures(mats.puddleMask, mats.puddleNormal);
    lampsView.setRipple(mats.waterNormal);
  });

  // Mood: how lit the windows, lamps and signs are (day vs dusk).
  const unsubMood = onMood((m) => {
    mats.facade.emissiveIntensity = 1.6 * m.windows;
    mats.setWindowLevel(m.windows);
    const lamp = Math.max(0.3, m.lamps);
    mats.glow.color.setScalar(lamp);
    // lit panes of the street models (the phone box): about as bright as the procedural pane boxes after the bloom
    street.uStreetGlow.value = 0.9 * Math.max(0.1, m.lamps);
    mats.pools.color.setScalar(m.lamps);
    mats.litSigns.emissiveIntensity = 1.6 * Math.max(0.35, m.windows);
    mats.signs.emissiveIntensity = 0.28 * Math.max(0.5, m.windows);
    signalsView.setLevel(m.lamps);
    lampsView.setLevel(m.lamps);
    haze.setMood(m.haze.color, m.haze.opacity);
    skyline.setMood(m);
    sky.setMood(m);
    // The cut-away's floor fill: none by day, most of it at dusk, all of it at night.
    occluder.setFillLevel((m.lamps - 0.15) / 1.1);
    puddles.setSky(m.sky.mid);
  });

  /** Wet roads: glossier and darker as the rain sets in, puddles fill; they dry out slowly. */
  const applyWetness = (w: number): void => {
    wetness = w;
    mats.setWetness(w);
    puddles.setWetness(w);
    lampsView.setWetness(w);
    signalsView.setWetness(w);
  };

  return {
    group,
    materials: mats,
    lamps: lampsView,
    occluder,
    knockdowns,
    signalHeads: signalsView,
    update(time, dt) {
      mats.tick(time);
      knockdowns.update(dt);
      const target = weatherState.current === 'rain' ? 1 : weatherState.current === 'drizzle' ? 0.6 : 0;
      if (wetness < 0) applyWetness(target);
      else if (Math.abs(target - wetness) > 0.002) applyWetness(wetness + (target - wetness) * Math.min(1, dt * (target > wetness ? 0.6 : 0.15)));
      haze.update(time);
      skyline.update(time);
      sky.update(time);
      signalsView.update();
      lampsView.update(dt);
      // Belisha beacons blink at ~1 Hz.
      const on = Math.floor(time * 1.6) % 2 === 0;
      beaconMat.color.setRGB(on ? 3.4 : 0.35, on ? 1.7 : 0.18, on ? 0.25 : 0.03);
    },
    stats() {
      return { meshes: group.children.length, triangles: Math.round(triangles), chunks: chunks.size, streetLife: life.stats, wetness: Math.max(0, wetness), skyline: skyline.stats() };
    },
    dispose() {
      unsubMood();
      unsubTier();
      unsubLampTier();
      unsubSkyTier();
      scene.remove(sky.mesh);
      sky.dispose();
      if (puddles.mesh) scene.remove(puddles.mesh);
      puddles.dispose();
      scene.remove(haze.mesh);
      haze.dispose();
      scene.remove(skyline.mesh);
      skyline.dispose();
      lampsView.dispose();
      knockdowns.dispose();
      scene.remove(group);
      for (const c of group.children) (c as THREE.Mesh).geometry.dispose();
      signalsView.dispose();
      beaconMat.dispose();
      mats.dispose();
    },
  };
}
